import React, { useState } from "react";
import { Box, Button, Checkbox, CircularProgress, Radio, Typography } from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  applyCanonicalEmail,
  getUserMergePreview,
  ManualReviewItem,
  ManualReviewUser,
  MergeGroup,
  MergeOrganization,
  mergeUserGroups,
  mergeUsersInto,
  UserMergePreview,
  UserMergeResult,
} from "../../../api/derin";
import { useToast } from "../Toast";
import { errorMessageOf } from "../errorMessage";
import ModalShell from "../CollectionsTab/ModalShell";
import {
  chipSx,
  collectionColors,
  font,
  outlinedButtonSx,
  primaryButtonSx,
  subtitleSx,
} from "../CollectionsTab/tokens";
import { roleChipSx } from "../Users/styles";

/** What the confirm dialog is about to run. */
type Pending =
  | { kind: "groups"; groups: MergeGroup[] }
  | { kind: "explicit"; into: ManualReviewUser; merge: ManualReviewUser[] }
  | { kind: "rename"; user: ManualReviewUser; email: string };

const cardSx = {
  display: "flex",
  flexDirection: "column" as const,
  gap: "8px",
  padding: "14px 16px",
  border: `1px solid ${collectionColors.border}`,
  borderRadius: "10px",
};

const labelSx = { ...subtitleSx, fontSize: "13px", minWidth: "110px" };
const valueSx = { fontFamily: font, fontSize: "13px", color: collectionColors.textPrimary };

const Line: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap" }}>
    <Typography sx={labelSx}>{label}</Typography>
    <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" }}>{children}</Box>
  </Box>
);

const Organizations: React.FC<{ organizations: MergeOrganization[] }> = ({ organizations }) =>
  organizations.length === 0 ? (
    <Typography sx={valueSx}>None</Typography>
  ) : (
    <>
      {organizations.map((o) => (
        <Box key={o.id} component="span" sx={{ display: "inline-flex", gap: "4px", alignItems: "center" }}>
          <Typography component="span" sx={valueSx}>
            {o.name}
          </Typography>
          <Box component="span" sx={roleChipSx(o.role)}>
            {o.role}
          </Box>
        </Box>
      ))}
    </>
  );

const inactive = (isActive: boolean) =>
  isActive ? null : (
    <Box component="span" sx={{ ...chipSx, display: "inline-flex", color: collectionColors.textSecondary }}>
      inactive
    </Box>
  );

const movedSummary = (moved: MergeGroup["moved"]) => {
  const parts = [
    [moved.feedbackSessions, "feedback sessions"],
    [moved.feedbacks, "feedbacks"],
    [moved.cohorts, "cohorts"],
    [moved.auditEvents, "audit events"],
  ].filter(([count]) => count) as [number, string][];
  return parts.length ? parts.map(([count, label]) => `${count} ${label}`).join(" · ") : "Nothing";
};

const GroupDetails: React.FC<{ group: MergeGroup }> = ({ group }) => (
  <>
    <Line label="Keeps">
      <Typography sx={valueSx}>{group.survivor.email}</Typography>
      {inactive(group.survivor.isActive)}
      {group.survivor.email !== group.email && (
        <Typography sx={valueSx}>→ {group.email}</Typography>
      )}
    </Line>
    <Line label="Merges">
      {group.merged.map((m) => (
        <Box key={m.id} component="span" sx={{ display: "inline-flex", gap: "4px", alignItems: "center" }}>
          <Typography component="span" sx={valueSx}>
            {m.email}
          </Typography>
          {inactive(m.isActive)}
        </Box>
      ))}
    </Line>
    <Line label="Organizations">
      <Organizations organizations={group.organizations} />
    </Line>
    <Line label="Password">
      <Typography sx={valueSx}>
        {group.passwordFrom ? `from ${group.passwordFrom}` : "None (needs a reset to sign in)"}
      </Typography>
    </Line>
    <Line label="Moves">
      <Typography sx={valueSx}>{movedSummary(group.moved)}</Typography>
    </Line>
  </>
);

/**
 * "Fix users": merges the accounts one person needed before memberships existed
 * (`jane+acme@…`, `jane+beta@…`) into one user with access to every organization.
 * Each group is approved on its own; cases a rule can't settle are listed for a
 * decision. The Derin API runs it against the environment this console points to,
 * and only for the operators it allows.
 */
const FixUsersCard: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [preview, setPreview] = useState<UserMergePreview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [keepByItem, setKeepByItem] = useState<Record<number, string>>({});
  const [pending, setPending] = useState<Pending | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<UserMergeResult | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  const loadPreview = async () => {
    setIsLoading(true);
    try {
      const next = await getUserMergePreview();
      setPreview(next);
      setSelected(new Set(next.groups.map((g) => g.canonical)));
      setKeepByItem({});
    } catch (err) {
      showError(`Error checking users: ${errorMessageOf(err)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const groups = preview?.groups ?? [];
  const manual = preview?.manualReview ?? [];
  const approved = groups.filter((g) => selected.has(g.canonical));

  const toggle = (canonical: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(canonical)) next.delete(canonical);
      else next.add(canonical);
      return next;
    });

  const run = async () => {
    if (!pending) return;
    setIsRunning(true);
    try {
      if (pending.kind === "rename") {
        const renamed = await applyCanonicalEmail(pending.user.id);
        showSuccess(`${pending.user.email} is now ${renamed.email}`);
      } else {
        const outcome =
          pending.kind === "groups"
            ? await mergeUserGroups(pending.groups)
            : await mergeUsersInto(
                pending.into.id,
                pending.merge.map((u) => u.id)
              );
        setResult(outcome);
        showSuccess(
          `${outcome.applied.length} merge${outcome.applied.length === 1 ? "" : "s"} applied` +
            (outcome.skipped.length ? `, ${outcome.skipped.length} skipped` : "")
        );
      }
      setPending(null);
      await loadPreview();
    } catch (err) {
      showError(errorMessageOf(err));
    } finally {
      setIsRunning(false);
    }
  };

  const renderManualItem = (item: ManualReviewItem, index: number) => {
    if (item.kind === "alias-only") {
      const user = item.users[0];
      return (
        <Box key={`alias-${user.id}`} sx={cardSx} data-testid="manual-alias-only">
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", justifyContent: "space-between", flexWrap: "wrap" }}>
            <Box>
              <Typography sx={{ ...valueSx, fontWeight: 500, fontSize: "14px" }}>{user.email}</Typography>
              <Typography sx={{ ...subtitleSx, fontSize: "13px" }}>
                {user.name} · {item.reason}
              </Typography>
            </Box>
            <Button
              sx={outlinedButtonSx}
              onClick={() => setPending({ kind: "rename", user, email: item.suggestedEmail })}
            >
              Use {item.suggestedEmail}
            </Button>
          </Box>
          <Line label="Organizations">
            <Organizations organizations={user.organizations} />
          </Line>
        </Box>
      );
    }

    const keepId = keepByItem[index];
    const into = item.users.find((u) => u.id === keepId);
    return (
      <Box key={`dup-${item.users.map((u) => u.id).join("-")}`} sx={cardSx} data-testid="manual-possible-duplicate">
        <Typography sx={{ ...subtitleSx, fontSize: "13px" }}>{item.reason}</Typography>
        {item.users.map((user) => (
          <Box key={user.id} sx={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
            <Radio
              size="small"
              checked={keepId === user.id}
              onChange={() => setKeepByItem((current) => ({ ...current, [index]: user.id }))}
              inputProps={{ "aria-label": `Keep ${user.email}` }}
              sx={{ padding: "2px" }}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography sx={{ ...valueSx, fontWeight: 500, fontSize: "14px" }}>
                {user.email} <Box component="span" sx={{ color: collectionColors.textSecondary, fontWeight: 400 }}>· {user.name}</Box>
              </Typography>
              <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                <Organizations organizations={user.organizations} />
              </Box>
            </Box>
          </Box>
        ))}
        <Box>
          <Button
            sx={outlinedButtonSx}
            disabled={!into}
            onClick={() =>
              into &&
              setPending({ kind: "explicit", into, merge: item.users.filter((u) => u.id !== into.id) })
            }
          >
            {into ? `Merge into ${into.email}` : "Pick the account to keep"}
          </Button>
        </Box>
      </Box>
    );
  };

  const confirmText = (): { title: string; subtitle: string; label: string } => {
    if (!pending) return { title: "", subtitle: "", label: "" };
    if (pending.kind === "groups") {
      const accounts = pending.groups.reduce((sum, g) => sum + g.merged.length, 0);
      return {
        title: `Merge ${pending.groups.length} group${pending.groups.length === 1 ? "" : "s"}?`,
        subtitle: `${accounts} account${accounts === 1 ? "" : "s"} will be deleted and their organizations and data moved to the account kept. This cannot be undone.`,
        label: "Merge",
      };
    }
    if (pending.kind === "explicit") {
      return {
        title: `Merge into ${pending.into.email}?`,
        subtitle: `${pending.merge.map((u) => u.email).join(", ")} will be deleted and their organizations and data moved to ${pending.into.email}, which keeps its email and password. This cannot be undone.`,
        label: "Merge",
      };
    }
    return {
      title: `Change email to ${pending.email}?`,
      subtitle: `${pending.user.email} will sign in with ${pending.email} from now on. The password stays the same.`,
      label: "Change email",
    };
  };
  const confirm = confirmText();

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        width: "100%",
        background: collectionColors.white,
        border: `1px solid ${collectionColors.border}`,
        borderRadius: "14px",
        padding: "25px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "16px", justifyContent: "space-between", flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1, minWidth: "280px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <PeopleAltOutlinedIcon sx={{ width: 18, height: 18, color: collectionColors.textSecondary }} />
            <Typography sx={{ fontFamily: font, fontWeight: 500, fontSize: "18px", lineHeight: "27px", color: collectionColors.textPrimary }}>
              Fix duplicate users
            </Typography>
          </Box>
          <Typography sx={{ ...subtitleSx, fontSize: "16px", lineHeight: "24px" }}>
            Before a user could belong to several organizations, people had one account per
            organization (name+acme@…, name+beta@…). This merges each person's accounts into one
            with access to all of their organizations. Review each group before merging.
          </Typography>
        </Box>
        <Button
          sx={primaryButtonSx}
          onClick={loadPreview}
          disabled={isLoading || isRunning}
          startIcon={isLoading ? <CircularProgress size={14} sx={{ color: collectionColors.white }} /> : undefined}
        >
          {preview ? "Check again" : "Check users"}
        </Button>
      </Box>

      {preview && (
        <>
          <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <Box sx={chipSx}>{preview.totalUsers} accounts</Box>
            <Box sx={chipSx}>{preview.activeUsers} active</Box>
            <Box sx={{ ...chipSx, ...(groups.length ? { background: collectionColors.buttonAccent, border: "1px solid transparent" } : {}) }}>
              {groups.length} group{groups.length === 1 ? "" : "s"} to merge
            </Box>
            <Box sx={chipSx}>{manual.length} need a decision</Box>
          </Box>

          {groups.length === 0 ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <CheckCircleOutlineIcon sx={{ width: 18, height: 18, color: "#16A34A" }} />
              <Typography sx={subtitleSx}>No +alias accounts left to merge.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <Typography sx={{ ...valueSx, fontWeight: 500, fontSize: "14px" }}>
                  Groups to merge
                </Typography>
                <Button sx={{ ...outlinedButtonSx, height: "28px" }} onClick={() => setSelected(new Set(groups.map((g) => g.canonical)))}>
                  Select all
                </Button>
                <Button sx={{ ...outlinedButtonSx, height: "28px" }} onClick={() => setSelected(new Set())}>
                  Select none
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button
                  sx={primaryButtonSx}
                  disabled={approved.length === 0 || isRunning}
                  onClick={() => setPending({ kind: "groups", groups: approved })}
                >
                  Merge {approved.length} selected
                </Button>
              </Box>
              {groups.map((group) => (
                <Box key={group.canonical} sx={{ ...cardSx, flexDirection: "row", alignItems: "flex-start" }} data-testid="merge-group">
                  <Checkbox
                    size="small"
                    checked={selected.has(group.canonical)}
                    onChange={() => toggle(group.canonical)}
                    inputProps={{ "aria-label": `Merge ${group.canonical}` }}
                    sx={{ padding: "2px" }}
                  />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", flex: 1, minWidth: 0 }}>
                    <Typography sx={{ ...valueSx, fontWeight: 600, fontSize: "14px" }}>
                      {group.survivor.name} · {group.canonical}
                    </Typography>
                    <GroupDetails group={group} />
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {manual.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <Box>
                <Typography sx={{ ...valueSx, fontWeight: 500, fontSize: "14px" }}>Needs a decision</Typography>
                <Typography sx={{ ...subtitleSx, fontSize: "13px" }}>
                  Not merged automatically. Lone +alias accounts can drop the tag from their email;
                  possible duplicates are merged only if you pick the account to keep.
                </Typography>
              </Box>
              {manual.map(renderManualItem)}
            </Box>
          )}
        </>
      )}

      {result && (
        <Box
          data-testid="merge-result"
          sx={{ display: "flex", flexDirection: "column", gap: "8px", padding: "16px", background: collectionColors.surfaceLight, borderRadius: "10px" }}
        >
          <Typography sx={{ ...valueSx, fontWeight: 500, fontSize: "14px" }}>
            Last run: {result.applied.length} merged · {result.skipped.length} skipped · now{" "}
            {result.after.activeUsers} active accounts, {result.after.memberships} memberships
          </Typography>
          {result.applied.map((group) => (
            <Typography key={group.survivor.id} sx={{ ...subtitleSx, fontSize: "13px" }}>
              ✓ {group.email} ← {group.merged.map((m) => m.email).join(", ")} ·{" "}
              {group.organizations.length} organization{group.organizations.length === 1 ? "" : "s"}
            </Typography>
          ))}
          {result.skipped.map((s) => (
            <Typography key={`skipped-${s.canonical}`} sx={{ ...subtitleSx, fontSize: "13px", color: collectionColors.danger }}>
              ✕ {s.canonical}: {s.reason}
            </Typography>
          ))}
          <Box>
            <Button sx={{ ...outlinedButtonSx, height: "28px" }} onClick={() => setShowRaw((v) => !v)}>
              {showRaw ? "Hide output" : "Show output"}
            </Button>
          </Box>
          {showRaw && (
            <Box
              component="pre"
              sx={{ margin: 0, padding: "12px", maxHeight: "400px", overflow: "auto", background: collectionColors.white, border: `1px solid ${collectionColors.border}`, borderRadius: "8px", fontSize: "12px" }}
            >
              {JSON.stringify(result, null, 2)}
            </Box>
          )}
        </Box>
      )}

      <ModalShell
        open={!!pending}
        onClose={() => setPending(null)}
        title={confirm.title}
        subtitle={confirm.subtitle}
        confirmLabel={confirm.label}
        onConfirm={run}
        isSubmitting={isRunning}
        danger={pending?.kind !== "rename"}
      />
    </Box>
  );
};

export default FixUsersCard;
